import { useState } from "react";
import { ALL_PERM_DEFS, type PermDef } from "@/features/identity-hub/lib/permission-catalog";

export interface PermissionPayloadItem {
  type: PermDef["type"];
  method: PermDef["method"];
  route: string;
}

/** Stable key for deduping permission tuples (DB unique is accessId+type+method+route). */
export function permissionTupleKey(perm: { type: string; method: string; route: string }): string {
  return `${perm.type}|${perm.method}|${perm.route}`;
}

/** Catalog keys that map to the same stored permission tuple (e.g. shared `/uploads/*` routes). */
const CATALOG_KEYS_BY_TUPLE = (() => {
  const map = new Map<string, string[]>();
  for (const def of ALL_PERM_DEFS) {
    const tupleKey = permissionTupleKey(def);
    const keys = map.get(tupleKey) ?? [];
    keys.push(def.key);
    map.set(tupleKey, keys);
  }
  return map;
})();

function aliasCatalogKeys(key: string): string[] {
  const def = ALL_PERM_DEFS.find((d) => d.key === key);
  if (!def) return [key];
  return CATALOG_KEYS_BY_TUPLE.get(permissionTupleKey(def)) ?? [key];
}

/** Selected permission keys -> the `{ type, method, route }` payload the API expects. */
export function permissionKeysToPayload(keys: Set<string>): PermissionPayloadItem[] {
  const byTuple = new Map<string, PermissionPayloadItem>();
  for (const def of ALL_PERM_DEFS) {
    if (!keys.has(def.key)) continue;
    const tupleKey = permissionTupleKey(def);
    if (!byTuple.has(tupleKey)) {
      byTuple.set(tupleKey, { type: def.type, method: def.method, route: def.route });
    }
  }
  return Array.from(byTuple.values());
}

/** A role's granted `{ type, method, route }` permissions -> catalog keys, for seeding selection state. */
export function permissionPayloadToKeys(permissions: Array<{ type: string; method: string; route: string }>): Set<string> {
  const keys = new Set<string>();
  for (const perm of permissions) {
    for (const key of CATALOG_KEYS_BY_TUPLE.get(permissionTupleKey(perm)) ?? []) {
      keys.add(key);
    }
  }
  return keys;
}

interface UsePermissionSelectionOptions {
  /** Fired on every toggle/group/remove — e.g. to clear an active preset selection. */
  onChange?: () => void;
}

/**
 * Selection state for the permission catalog checkboxes, shared by NewRoleDialog
 * and EditPermissionsDialog: toggle a single permission, toggle a whole action
 * group, or bulk-remove keys (used when revoking page access cascades to its
 * action permissions).
 */
export function usePermissionSelection(initial: Set<string> = new Set(), { onChange }: UsePermissionSelectionOptions = {}) {
  const [selected, setSelected] = useState<Set<string>>(initial);

  const togglePerm = (key: string) => {
    onChange?.();
    const aliases = aliasCatalogKeys(key);
    setSelected((prev) => {
      const next = new Set(prev);
      const turningOn = !prev.has(key);
      for (const alias of aliases) {
        if (turningOn) next.add(alias);
        else next.delete(alias);
      }
      return next;
    });
  };

  const toggleGroup = (keys: string[]) => {
    onChange?.();
    const allSelected = keys.every((k) => selected.has(k));
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) keys.forEach((k) => next.delete(k));
      else keys.forEach((k) => next.add(k));
      return next;
    });
  };

  const removeKeys = (keys: string[]) => {
    onChange?.();
    setSelected((prev) => {
      const next = new Set(prev);
      for (const k of keys) next.delete(k);
      return next;
    });
  };

  return { selected, setSelected, togglePerm, toggleGroup, removeKeys, selectedCount: selected.size };
}
